import { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { settings as settingsApi } from '../../api';
import { API_BASE } from '../../api/core';
import { useAuth } from '../../context/AuthContext';

const loadLoginModal = () => import('./LoginModal');
const LoginModal = lazy(loadLoginModal);

const MAINTENANCE_CHECKOUT_BYPASS_KEY = 'maintenance_checkout_bypass';

const DEFAULT_MAINTENANCE = {
  enabled: false,
  imageUrl: '',
  title: '',
  message: '',
};

const FALLBACK_MAINTENANCE = {
  enabled: true,
  imageUrl: 'https://pub-524d3a1fe7b24cf8a3b10a5b6b218ab2.r2.dev/maintenance/2ce894f5-4a7a-429e-b150-43db56bc8423-maintenance-1783671153990.webp',
  title: 'Estamos realizando mantenimiento',
  message: 'El servicio volverá a estar disponible en breve.',
};

function normalizeMaintenance(value) {
  if (!value || typeof value !== 'object') return DEFAULT_MAINTENANCE;
  return {
    enabled: value.enabled === true,
    imageUrl: typeof value.imageUrl === 'string' ? value.imageUrl : '',
    title: typeof value.title === 'string' ? value.title : '',
    message: typeof value.message === 'string' ? value.message : '',
  };
}

function readEmbeddedMaintenance() {
  if (typeof document === 'undefined') return null;
  try {
    const raw = document.getElementById('maintenance-mode-snapshot')?.textContent;
    return raw ? normalizeMaintenance(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
}

function readAdminAccessIntent() {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);
  return params.get('admin') === 'true';
}

function MaintenanceScreen({ config }) {
  const [loginOpen, setLoginOpen] = useState(readAdminAccessIntent);
  const [showAdminAccess, setShowAdminAccess] = useState(readAdminAccessIntent);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const wantsAdmin = params.get('admin') === 'true';
    setShowAdminAccess(wantsAdmin);
    setLoginOpen(wantsAdmin);
  }, []);

  const openAdminLogin = () => {
    if (typeof window === 'undefined') return;
    void loadLoginModal();
    const params = new URLSearchParams(window.location.search);
    params.set('admin', 'true');
    window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`);
    setShowAdminAccess(true);
    setLoginOpen(true);
  };

  const closeAdminLogin = () => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    params.delete('admin');
    params.delete('redirect');
    const search = params.toString();
    window.history.replaceState({}, '', `${window.location.pathname}${search ? `?${search}` : ''}`);
    setShowAdminAccess(false);
    setLoginOpen(false);
  };

  return (
    <>
      <div className="relative h-[100dvh] min-h-screen w-full overflow-hidden bg-white">
        {config.imageUrl ? (
          <div className="absolute inset-0 flex h-full w-full items-center justify-center bg-white">
            <img
              src={config.imageUrl}
              alt={config.title || 'Mantenimiento'}
              className="h-full w-full object-cover"
              draggable="false"
            />
          </div>
        ) : null}
        {showAdminAccess ? (
          <div className="absolute bottom-6 left-1/2 z-20 flex -translate-x-1/2 flex-col items-center gap-2 sm:flex-row sm:gap-3">
            <button
              type="button"
              onClick={openAdminLogin}
              className="rounded-2xl bg-black/80 px-5 py-2.5 text-[13px] font-bold text-white transition-colors hover:bg-[#FF50A3]"
            >
              Acceso administrador
            </button>
            <a
              href="/admin"
              className="rounded-2xl border border-white/60 bg-black/40 px-5 py-2.5 text-[13px] font-bold text-white transition-colors hover:bg-white/10"
            >
              Ir a /admin
            </a>
          </div>
        ) : null}
      </div>
      {loginOpen ? (
        <Suspense fallback={null}>
          <LoginModal isOpen onClose={closeAdminLogin} />
        </Suspense>
      ) : null}
    </>
  );
}

export default function MaintenanceGate({ children }) {
  const location = useLocation();
  const { user, loading } = useAuth();
  const [initialConfig] = useState(readEmbeddedMaintenance);
  const [config, setConfig] = useState(initialConfig || DEFAULT_MAINTENANCE);
  const [configLoading, setConfigLoading] = useState(!initialConfig);
  const [availabilityError, setAvailabilityError] = useState(false);
  const [serviceUnavailable, setServiceUnavailable] = useState(false);
  const [checkoutBypassEnabled, setCheckoutBypassEnabled] = useState(() => {
    if (typeof window === 'undefined') return false;
    return sessionStorage.getItem(MAINTENANCE_CHECKOUT_BYPASS_KEY) === 'true';
  });

  useEffect(() => {
    let alive = true;
    settingsApi.get('maintenance_mode')
      .then((res) => {
        if (!alive) return;
        setAvailabilityError(false);
        setConfig(normalizeMaintenance(res?.value));
      })
      .catch(() => {
        if (alive) {
          setAvailabilityError(true);
          setConfig(FALLBACK_MAINTENANCE);
        }
      })
      .finally(() => {
        if (alive) setConfigLoading(false);
      });

    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    let alive = true;
    let consecutiveFailures = 0;
    const healthUrl = `${API_BASE.replace(/\/api\/?$/, '')}/health`;
    const checkHealth = async () => {
      try {
        const response = await fetch(healthUrl, { cache: 'no-store' });
        if (!response.ok) throw new Error('Servicio no disponible');
        consecutiveFailures = 0;
        if (alive) {
          setServiceUnavailable(false);
          setAvailabilityError(false);
        }
      } catch {
        consecutiveFailures += 1;
        if (alive && consecutiveFailures >= 2) setServiceUnavailable(true);
      }
    };

    void checkHealth();
    const timer = window.setInterval(checkHealth, 15_000);
    return () => {
      alive = false;
      window.clearInterval(timer);
    };
  }, []);

  const isAdminUser = useMemo(() => {
    const role = String(user?.role || '').toUpperCase();
    return role === 'ADMIN' || role === 'COLLABORATOR';
  }, [user?.role]);

  const isAdminRoute = location.pathname.startsWith('/admin');
  const isCheckoutFlowRoute = location.pathname.startsWith('/checkout');

  useEffect(() => {
    if (typeof window === 'undefined' || isAdminUser) return;

    if (config.enabled && isCheckoutFlowRoute) {
      sessionStorage.setItem(MAINTENANCE_CHECKOUT_BYPASS_KEY, 'true');
      setCheckoutBypassEnabled(true);
      return;
    }

    if (!isCheckoutFlowRoute && sessionStorage.getItem(MAINTENANCE_CHECKOUT_BYPASS_KEY) === 'true') {
      sessionStorage.removeItem(MAINTENANCE_CHECKOUT_BYPASS_KEY);
      setCheckoutBypassEnabled(false);
    }
  }, [config.enabled, isAdminUser, isCheckoutFlowRoute, location.pathname]);

  if ((loading || configLoading) && !isAdminRoute) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-10 h-10 border-4 border-[#FF50A3] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if ((availabilityError || serviceUnavailable || config.enabled) && !isAdminUser && !isAdminRoute && !(checkoutBypassEnabled && isCheckoutFlowRoute)) {
    return <MaintenanceScreen config={config} />;
  }

  return children;
}
